import clsx from 'clsx';
import { useFormContext } from 'react-hook-form';
import { PaymentMethodCard } from 'src/components/PaymentMethod';
import { BuyerCard } from 'src/components/Selectors/BuyerSelector/BuyerCard';
import { useAppContext } from 'src/contexts/AppContext';
import { useContent } from 'src/contexts/ContentContext';
import { Radio, RadioGroup, Stack } from 'src/core/ui';
import { useGetBuyerPaymentMethods } from 'src/hooks/api/useGetBuyerPaymentMethods';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { SectionContent } from 'src/modals/common/Detail/Detail.styled';
import { DetailSection } from 'src/modals/common/DetailSection';
import { Modal } from 'src/modals/Modal';
import { useRetailSaleContext } from 'src/modals/RetailSale/RetailSaleLocalContext';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature } from 'src/WebApiController';

import { PaymentMethodChoice, RetailSaleFormValues } from '../types';
import { ExternalPaymentSection } from './ExternalPaymentSection';
import { PartialPaymentCoverageDialog } from './PartialPaymentCoverageDialog';
import { PartialPaymentInput } from './PartialPaymentInput';
import { RetailSaleEarningsSection } from './RetailSaleCheckoutStep.components';
import * as styles from './RetailSaleCheckoutStep.css';

export const RetailSaleCheckoutStep = () => {
  const {
    selectedBuyer,
    disabled,
    selectedStoredPaymentMethodId,
    setSelectedStoredPaymentMethodId,
    isPartialPayment,
    setIsPartialPayment,
    partialAmount,
    setPartialAmount,
    coverageDialogSaleId,
    onCoverageConfirmed,
    onCoverageDialogCancel,
  } = useRetailSaleContext();

  const { loginContext } = useAppContext();
  const { watch, setValue } = useFormContext<RetailSaleFormValues>();
  const totalNetProceeds = watch('totalNetProceeds');
  const marketplacePaymentLines = watch('marketplacePaymentLines');
  const paymentMethodChoice = watch('paymentMethodChoice') ?? 'adyen';
  const currencyCode =
    watch('currencyCode') ??
    loginContext?.user?.activeAccount.currencyCode ??
    'USD';

  const totalAmount =
    (totalNetProceeds ?? 0) +
    (marketplacePaymentLines ?? []).reduce(
      (sum, l) => sum + (l.amount ?? 0),
      0
    );
  const customerInfoMsg = useContent(ContentId.CustomerInfo);
  const paymentMethodMsg = useContent(ContentId.PaymentMethod);
  const addNewCardMsg = useContent(ContentId.AddNewCard);

  const { data: savedPaymentMethods = [] } = useGetBuyerPaymentMethods(
    selectedBuyer?.buyerEmail
  );

  // Gate the "Paid Externally" option behind the RetailSale feature flag.
  // Feature.RetailSale (value 725) controls access to retail sale functionality.
  const hasRetailSaleFeature = useUserHasFeature(Feature.RetailSale);

  const isExternalPayment = paymentMethodChoice === 'external';

  const handlePaymentChoiceChange = (value: string) => {
    const choice = value as PaymentMethodChoice;
    setValue('paymentMethodChoice', choice);

    // When switching to external, pre-populate amount with today's date
    if (choice === 'external') {
      const today = new Date().toISOString().split('T')[0];
      setValue('externalPaymentDate', today);
      setValue('externalPaymentAmount', totalAmount > 0 ? totalAmount : null);
    } else {
      // Clear external payment fields when switching back to Adyen
      setValue('externalPaymentMethodType', null);
      setValue('externalPaymentNote', null);
      setValue('externalPaymentAmount', null);
      setValue('externalPaymentDate', null);
    }
  };

  return (
    <Stack direction="column" gap="l">
      {/* Customer Info Card */}
      {selectedBuyer && (
        <DetailSection name={customerInfoMsg}>
          <SectionContent numOfColumns={1}>
            <BuyerCard buyer={selectedBuyer} />
          </SectionContent>
        </DetailSection>
      )}

      {/* EARNINGS section */}
      <RetailSaleEarningsSection disabled={disabled} />

      {/* PAYMENT METHOD section */}
      <DetailSection name={paymentMethodMsg}>
        <Stack direction="column" gap="m">
          {/* Payment mode selection — only show "Paid Externally" when feature flag is on */}
          {hasRetailSaleFeature && (
            <RadioGroup
              value={paymentMethodChoice}
              onValueChange={handlePaymentChoiceChange}
              disabled={disabled}
            >
              <Radio value="adyen" label="Paid in POS" disabled={disabled} />
              <Radio
                value="external"
                label="Paid Externally (Cash / Wire / Other)"
                disabled={disabled}
              />
            </RadioGroup>
          )}

          {/* PARTIAL PAYMENT section — visible for BOTH Adyen and External flows */}
          <PartialPaymentInput
            disabled={disabled}
            totalAmount={totalAmount}
            currencyCode={currencyCode}
            isPartialPayment={isPartialPayment}
            partialAmount={partialAmount}
            onIsPartialPaymentChange={setIsPartialPayment}
            onPartialAmountChange={setPartialAmount}
          />

          {/* Adyen flow — preserved exactly as before */}
          {!isExternalPayment && (
            <div className={styles.savedCardsSection}>
              {savedPaymentMethods.map((card) => {
                const cardId = card.storedPaymentMethodId;
                const isSelected = selectedStoredPaymentMethodId === cardId;
                return (
                  <div
                    key={cardId}
                    role="radio"
                    aria-checked={isSelected}
                    tabIndex={0}
                    className={clsx(styles.savedCardRow, {
                      [styles.savedCardRowSelected]: isSelected,
                    })}
                    onClick={
                      disabled
                        ? undefined
                        : () => setSelectedStoredPaymentMethodId(cardId)
                    }
                    onKeyDown={
                      disabled
                        ? undefined
                        : (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              setSelectedStoredPaymentMethodId(cardId);
                            }
                          }
                    }
                  >
                    <input
                      type="radio"
                      readOnly
                      checked={isSelected}
                      aria-hidden="true"
                    />
                    <PaymentMethodCard
                      cardBrand={card.cardBrand}
                      cardSummary={card.cardSummary}
                      expiryMonth={card.expiryMonth}
                      expiryYear={card.expiryYear}
                      isDefault={card.isDefault}
                    />
                  </div>
                );
              })}

              {/* Add a new card option */}
              <button
                type="button"
                onClick={() => {
                  setSelectedStoredPaymentMethodId(null);
                }}
                className={clsx(styles.addNewCardRow, {
                  [styles.addNewCardRowSelected]:
                    selectedStoredPaymentMethodId === null,
                })}
              >
                <input
                  type="radio"
                  readOnly
                  checked={selectedStoredPaymentMethodId === null}
                  aria-hidden="true"
                />
                {addNewCardMsg}
              </button>
            </div>
          )}

          {/* External payment form — shown when "Paid Externally" is selected */}
          {isExternalPayment && (
            <ExternalPaymentSection
              disabled={disabled}
              totalAmount={totalAmount}
            />
          )}
        </Stack>
      </DetailSection>

      {coverageDialogSaleId != null && (
        <Modal isOpen onClose={onCoverageDialogCancel}>
          <PartialPaymentCoverageDialog
            saleId={coverageDialogSaleId}
            partialAmount={partialAmount}
            currencyCode={currencyCode}
            onConfirm={onCoverageConfirmed}
            onCancel={onCoverageDialogCancel}
          />
        </Modal>
      )}
    </Stack>
  );
};
